import { GraphColor, LocalGraphColour } from "../Colors";

import { API_URL as api } from "@/config/env";

export const urls = {
	userCountries: `${api}GetUserCountries?`,
	quotaStatus: `${api}Approval/GetScenarioStatus`,
	periods: `${api}Approval/GetPeriods`,
	quotaForApproval: `${api}Approval/GetQuotaforApproval`,
	quotaCustomerBreakdown: `${api}Approval/GetQuotaCustomerBreakdown?`,
	approve: `${api}Approval/ApproveQuota`,
	approveQuotaWithComments: `${api}Approval/ApproveQuotaWithComments`,
	reject: `${api}Approval/RejectQuota`,
	popupGraphData: `${api}ApprovalQuotaModification/GetPopUpGraphData?`,
	lockUnlockQuota: `${api}Approval/LockUnlockQuota`,
	approveMultiple: `${api}Approval/ApproveQuotaMultiple`,
	lockUnlockMultiple: `${api}Approval/LockUnlockQuotaMultiple`,
	rejectMultiple: `${api}Approval/RejectQuotaMultiple`,
	approveMulWithComment: `${api}Approval/ApproveQuotaWithCommentsMultiple`,
	Downloadfile: `${api}Define/DownloadFile?`,
	Upload: `${api}Approve/Upload`,
};

function approvalQuotaNorm(v) {
	return String(v ?? "").trim().toLowerCase();
}

function categorizeApprovalQuotaRowDepletion(row, quotaPeriodDepletionModel) {
	const dmCode = row?.depletionModel ?? row?.DepletionModel;
	if (dmCode !== undefined && dmCode !== null && String(dmCode).trim() !== "") {
		const k = approvalQuotaNorm(dmCode);
		if (k === "none") return "none";
		if (k === "biweekly") return "biweekly";
		return "weekly";
	}
	const dmName = approvalQuotaNorm(
		row?.depletionModelName ?? row?.DepletionModelName
	);
	if (dmName === "none") return "none";
	if (dmName === "biweekly") return "biweekly";
	if (dmName !== "") return "weekly";
	const fb = approvalQuotaNorm(quotaPeriodDepletionModel);
	if (fb === "none") return "none";
	if (fb === "biweekly") return "biweekly";
	if (fb !== "") return "weekly";
	return "weekly";
}

/** Period text from selected approval quota row (4-week vs 5-week band). */
export function getApprovalQuotaBreakdownPeriodHint(approvalRow) {
	if (!approvalRow) return "";
	const a =
		approvalRow.depletionPeriodType ?? approvalRow.DepletionPeriodType ?? "";
	const b =
		approvalRow.depletionPeriodTypeName ??
		approvalRow.DepletionPeriodTypeName ??
		"";
	return `${a} ${b}`.trim();
}

/**
 * Approval breakdown grid layout:
 * - All rows none → baseline only (no week / half columns).
 * - All rows biweekly → First Half / Second Half only (no Week 1–5).
 * - Otherwise → Week 1–4 or 1–5 from period hint; mask cells per row when mix includes none/biweekly.
 */
export function deriveApprovalQuotaDetailsLayout(
	rows,
	periodTypeHint,
	quotaPeriodDepletionModel
) {
	if (!rows?.length) {
		return {
			layout: "baseline",
			showWeek5: false,
			applyWeekCellMasking: false,
		};
	}
	const cats = rows.map((r) =>
		categorizeApprovalQuotaRowDepletion(r, quotaPeriodDepletionModel)
	);
	const allNone = cats.every((c) => c === "none");
	const allBiweekly = cats.every((c) => c === "biweekly");
	if (allNone) {
		return {
			layout: "baseline",
			showWeek5: false,
			applyWeekCellMasking: false,
		};
	}
	if (allBiweekly) {
		return {
			layout: "biweekly",
			showWeek5: false,
			applyWeekCellMasking: false,
		};
	}
	const hint = String(periodTypeHint ?? "").toLowerCase();
	const showWeek5 = !hint.includes("4-week");
	const hasNone = cats.some((c) => c === "none");
	const hasBiweekly = cats.some((c) => c === "biweekly");
	/** Mixed grid with week bands: blank weeks that don't apply to none/biweekly rows (see approvalQuotaWeekDisplayValue). */
	const applyWeekCellMasking = hasNone || hasBiweekly;
	return {
		layout: "weekly",
		showWeek5,
		applyWeekCellMasking,
	};
}

const APPROVAL_QUOTA_WEEK_FIELDS = [
	"weekly1",
	"weekly2",
	"weekly3",
	"weekly4",
	"weekly5",
];

/** Week cell display in weekly layout: none → blank week 2+; biweekly → blank week 3+; otherwise show value. */
export function approvalQuotaWeekDisplayValue(
	row,
	weekIdx,
	quotaPeriodModel,
	applyWeekCellMasking
) {
	const field = APPROVAL_QUOTA_WEEK_FIELDS[weekIdx - 1];
	if (!field) return "";
	const raw = row?.[field];
	const fmt = (v) => {
		if (v === undefined || v === null || v === "") return "";
		const n = Number(v);
		if (Number.isNaN(n)) return String(v);
		return n.toLocaleString("en-US");
	};
	if (!applyWeekCellMasking) {
		return fmt(raw);
	}
	const cat = categorizeApprovalQuotaRowDepletion(row, quotaPeriodModel);
	if (cat === "none" && weekIdx >= 2 && weekIdx <= 5) return "";
	if (cat === "biweekly" && weekIdx >= 3 && weekIdx <= 5) return "";
	return fmt(raw);
}

export function getQuotaForApprovalTable(data) {
	const rows = getQuotaForApprovalRow(data);
	for (let i = 0; i < rows.length; i++) {
		rows[i] = getApproveStatus(rows[i]);
	}
	return { rows };
}

function getQuotaForApprovalRow(data) {
	const rows = data.map((item, index) => {
		return {
			id: index,
			quotaId: item.quotaId,
			scenarioId: item.scenarioId,
			scenarioName: item.scenarioName,
			periodType: item.periodType ?? item.PeriodType,
			geoId: item.geoId,
			criticalityId: item.criticalityId,
			graph: item.graph,
			quotaStatus: item.quotaStatus,
			depletionPeriodType: item.depletionPeriodType,
			depletionPeriodTypeName: item.depletionPeriodTypeName,
			depletionModelName: item.depletionModelName,
			initialForecast: item.initialForecast,
			totalForecast: item.totalForecast,
			previousTotalForecast: item.previousTotalForecast,
			difference: item.difference,
			depletion: item.depletion,
			previousDepletion: item.previousDepletion,
			lastActionDate: item.lastActionDate,
			lastActionBy: item.lastActionBy,
			period: item.period,
			rejectComments: item.rejectComments,
			approveComments: item.approveComments,
			isSubmitLocked: item.isSubmitLocked,
			edit: item.scenarioName,
			revisedQuota: item.revisedQuota,
			quotaDeviationPercentage:item.quotaDeviationPercentage,
			userCanApproveReject: item.userCanApproveReject,
			initialForecast_Define:item.initialForecast_Define,
			totalQuota_Define:item.totalQuota_Define,
			minQuota_Define:item.minQuota_Define,
			safetyMargin_Define:item.safetyMargin_Define,
			tender:item.tender,
			adjustment:item.adjustment,
			initialForecast_SetUp:item.initialForecast_SetUp,
			safetyMargin_SetUp:item.safetyMargin_SetUp,
			minQuota_SetUp:item.minQuota_SetUp,
			totalQuota_SetUp:item.totalQuota_SetUp,
		};
	});
	return rows;
}

export function getQuotaDetailsTable(data) {
	const columns = getQuotaDetailsColumn();
	const rows = getQuotaDetailsRow(data);
	return { columns, rows };
}

function getQuotaDetailsColumn() {
	const width = 150;
	const columns = [
		{ field: "customerName", headerName: "Customer / CG", width: width },
		{ field: "customerNumber", headerName: "Customer Number", width: width },
		{ field: "skuName", headerName: "SKU", width: width },
		{ field: "skuNumb", headerName: "SKU Number", width: width },
		{ field: "depletionModelName", headerName: "Depletion Type", width: width },
		{ field: "initialQuota", headerName: "Initial Quota", width: width },
		{ field: "safetyMargin", headerName: "Safety Margin", width: width },
		{ field: "adjustment", headerName: "Adjustment", width: width },
		{ field: "totalQuota", headerName: "Total Quota", width: width },
		{ field: "weekly1", headerName: "Week 1", width: width },
		{ field: "weekly2", headerName: "Week 2", width: width },
		{ field: "weekly3", headerName: "Week 3", width: width },
		{ field: "weekly4", headerName: "Week 4", width: width },
		{ field: "weekly5", headerName: "Week 5", width: width },
		{
			field: "customerQuotaComments",
			headerName: "Customer Quota Comments",
			width: width,
		},
	];
	return columns;
}

function getQuotaDetailsRow(data) {
	const rows = data.map((item, index) => {
		return {
			id: index,
			customerName: item.customerName,
			customerNumber: item.customerNumber,
			skuName: item.skuName,
			skuNumb: item.skuNumb,
			depletionModelName: item.depletionModelName,
			depletionModel: item.depletionModel ?? item.DepletionModel,
			initialQuota: item.initialQuota,
			safetyMargin: item.safetyMargin,
      		tender: item.tender,
			adjustment: item.adjustment,
			totalQuota: item.totalQuota,
			weekly1: item.weekly1,
			weekly2: item.weekly2,
			weekly3: item.weekly3,
			weekly4: item.weekly4,
			weekly5: item.weekly5,
			customerQuotaComments: item.customerQuotaComments,
		};
	});
	return rows;
}
function getApproveStatus(item) {
	debugger;
	let quotaStatus = item.quotaStatus.toLowerCase();
	let userCanApproveReject = item.userCanApproveReject;
	let quotaDeviationPercentage = item.quotaDeviationPercentage;
	let revisedQuota = item.revisedQuota;
	let totalForecast = item.totalForecast
	const logedUser = JSON.parse(localStorage.getItem("data"));
	if (logedUser.isAnalyst == true) {
		item["canApproveReject"] = false;
	} else {
		if (
			quotaStatus == "approved" ||
			quotaStatus == "rejected" ||
			quotaStatus == "draft"
		) {
			item["canApproveReject"] = false;
		} else {
			if (logedUser.isAdmin || (logedUser.isHQUser && logedUser.isApprover)) {
				if (quotaStatus == "submitted" || quotaStatus == "locally approved") {
					item["canApproveReject"] = true;
				}
			} else if (!logedUser.isHQUser) {
				if(userCanApproveReject==='true') {
					item["canApproveReject"] = true;
				}
				else{
					if((quotaDeviationPercentage!==0 && revisedQuota!==0) && (totalForecast<=(revisedQuota+(revisedQuota*quotaDeviationPercentage/100)))){
							item["canApproveReject"] = true;
				   }
				}
				// if (quotaStatus == "submitted") {
				// 	item["canApproveReject"] = true;
				// } else if (quotaStatus == "locally approved") {
				// 	item["canApproveReject"] = false;
				// }
			}
		}
	}
	return item;
}

/**
 * Scenario period type from approval list row (matches Define: hide row depletion when Weekly).
 */
export function scenarioApprovalPeriodTypeIsWeekly(approvalRow) {
	const pt =
		approvalRow?.periodType ?? approvalRow?.PeriodType ?? "";
	return String(pt).trim().toLowerCase() === "weekly";
}

export function preparePopupGraphData(data) {
	// The api returns last 19 months data for each data source. So first 20 items are
	// unique months
  // debugger;
  const numofMonths = 19
	console.log("preparePopupGraphData", data);
	const periods = data.slice(0, numofMonths).map((item) => item.period);
	const seriesData = [];
  console.log("data", data);
	const dataSourcesSet = new Set(
		data.map((item) =>
			JSON.stringify({
				dataSourceName: item.dataSourceName,
				datasourceID: item.datasourceID,
			})
		)
	);
  console.log("dataSourcesSet", dataSourcesSet);

	const dataSources = Array.from(dataSourcesSet).map((item) =>
		JSON.parse(item)
	);

  console.log("dataSources", dataSources);
	let start = 0,
		end = numofMonths;
	for (let d = 0; d < dataSources.length; ++d) {
		const dataSource = dataSources[d].dataSourceName;
		let values = [];
		for (let j = start; j < end; ++j) {
			values.push(data[j].value);
		}
		seriesData.push({ name: dataSource, data: values });
		start += numofMonths;
		end += numofMonths;
	}
	const duplicateDSForColor = data.map((item) => ({
		dataSourceName: item.dataSourceName,
		datasourceID: item.datasourceID,
	}));
	const uniqueSet = new Set(duplicateDSForColor.map(JSON.stringify));
	const dsr = Array.from(uniqueSet).map(JSON.parse);
	const color = [];
	for (let i = 0; i < dsr.length; ++i) {
		if (dsr[i].dataSourceName.toLowerCase().includes("iqvia - midas + direct to pharmacy sales")) {
			color.push(GraphColor["IQVIA - MIDAS + Direct To Pharmacy Sales"]);
		}
		else if (dsr[i].dataSourceName.toLowerCase().includes("ex-factory + direct to pharmacy sales")) {
			color.push(GraphColor["Ex-Factory + Direct To Pharmacy Sales"]);
		}
		else if (dsr[i].dataSourceName.toLowerCase().includes("direct to pharmacy sales")) {
			color.push(GraphColor["Direct To Pharmacy Sales"]);
		}				
		else if (dsr[i].dataSourceName.toLowerCase().includes("ex-factory")) {
			color.push(GraphColor["Ex-Factory"]);
		} else if (dsr[i].dataSourceName.toLowerCase().includes("iqvia - midas")) {
			color.push(GraphColor["IQVIA - MIDAS"]);
		}
		// else if(dsr[i].dataSourceName.toLowerCase().includes('ims')){
		// 	color.push(GraphColor.IMS);
		// }
		else if (dsr[i].dataSourceName.toLowerCase().includes("fin.fc-based")) {
			color.push(GraphColor["Fin.FC-based"]);
		}
		else if (dsr[i].dataSourceName.toLowerCase().includes("sc fc-based")) {
			color.push(GraphColor["SC FC-based"]);
		} 
		else if (dsr[i].dataSourceName.toLowerCase().includes("financial forecast")) {
			color.push(GraphColor["Financial Forecast"]);
		}
		else if (dsr[i].dataSourceName.toLowerCase().includes("sc forecast")) {
			color.push(GraphColor["SC Forecast"]);
		} 
		else if (dsr[i].dataSourceName.toLowerCase().includes("ai forecast")) {
			color.push(GraphColor["AI Forecast"]);
		} else if (dsr[i].dataSourceName.toLowerCase().includes("quota")) {
			color.push(GraphColor.Quota);
		} else if(dsr[i].dataSourceName.toLowerCase().includes("gers")){
			color.push(GraphColor.GERS);
		}
		// else if(dsr[i].dataSourceName.toLowerCase().includes('le diagnostics')){
		// 	color.push(GraphColor.LE);
		// }
		else if (dsr[i].dataSourceName.toLowerCase().includes("sit")) {
			color.push(GraphColor.SIT);
		} else if (dsr[i].datasourceID > 5 && dsr[i].datasourceID < 50) {
			color.push(LocalGraphColour[dsr[i].datasourceID]);
		} else {
			color.push("#16d620");
		}
	}

	const graphData = { periods, seriesData, color };
	console.log("Approval_Quota_preparePopupGraphData", graphData);
	return graphData;
}
